import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap-5';
import GenericList from './generic_list';
import { get } from 'helpers/api';
import { debounce } from 'lodash';
import qs from 'qs';

interface User {
  id: number;
  name: string;
}

interface UsersListProps {
  allowedUserIds?: number[] | null;
  companyId?: number;
  selectedUserIds: number[];
  onAddUser?: (id: number) => void;
  onRemoveUser?: (id: number) => void;
}

export default function UserLists({
  allowedUserIds = null,
  companyId,
  selectedUserIds,
  onAddUser,
  onRemoveUser
}: UsersListProps) {
  return (
    <Row>
      <Col md={6}>
        <div className="my-3">
          <strong className="fw-semibold">
            {allowedUserIds ? 'Users Available in Corporate Defaults' : 'All Available Users'}
          </strong>
        </div>
        {/* Only the pickable side is limited: users already granted access have to stay removable */}
        <UserList
          allowedUserIds={allowedUserIds}
          companyId={companyId}
          excludeUserIds={selectedUserIds || []}
          onAddUser={onAddUser}
        />
      </Col>
      <Col md={6}>
        <div className="my-3">
          <strong className="fw-semibold">Users with Access</strong>
        </div>
        <UserList companyId={companyId} includeUserIds={selectedUserIds || []} onRemoveUser={onRemoveUser}/>
      </Col>
    </Row>
  );
}

interface UserListProps {
  allowedUserIds?: number[] | null;
  companyId?: number;
  includeUserIds?: number[];
  excludeUserIds?: number[];
  onAddUser?: (id: number) => void;
  onRemoveUser?: (id: number) => void;
}
function UserList({
  allowedUserIds = null,
  companyId,
  includeUserIds,
  excludeUserIds,
  onAddUser,
  onRemoveUser
}: UserListProps) {
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);

  // Two searches can be in flight at once - the debounce only coalesces the calls it hasn't fired
  // yet - and they don't have to come back in order. Only the newest one is allowed to land, so a
  // slow failure can't put an error over a result that arrived after it.
  const latestRequest = useRef<number>(0);
  const [users, setUsers] = useState<User[]>([]);

  const searchUsers = useCallback(
    debounce((
      _searchText: string,
      _companyId?: number,
      _includeUserIds?: number[],
      _excludeUserIds?: number[],
      _allowedUserIds?: number[] | null
    ) => {
      const params: { limit?: number, [key: string]: any } = { limit: 15 };

      // Spelling the ids out — already granted, or the corporate allow-list —
      // makes the result a bounded set, so it doesn't need capping. Capping it
      // would hide the tail of a corporate list longer than the limit.
      if (_includeUserIds?.length || _allowedUserIds?.length) params.limit = undefined;

      if (_searchText) params['q[first_name_or_last_name_or_login_credential_email_cont]'] = _searchText;
      if (_companyId) params['company_id'] = _companyId;
      let queryString = qs.stringify(params);

      // Never combined with `_allowedUserIds`: both would land in the same
      // ransack `id_in` array and widen the result instead of narrowing it
      if (_includeUserIds) _includeUserIds.forEach((id: number) => {
        queryString += `&q[id_in][]=${id}`;
      });

      if (_allowedUserIds) _allowedUserIds.forEach((id: number) => {
        queryString += `&q[id_in][]=${id}`;
      });

      if (_excludeUserIds) _excludeUserIds.forEach((id: number) => {
        queryString += `&q[id_not_in][]=${id}`;
      });

      const request = ++latestRequest.current;

      get(`/users/search?${queryString}`).then((data: { users: User[] }) => {
        if (request !== latestRequest.current) return;

        setUsers(data.users);
        setLoading(false);
      }).catch(() => {
        if (request !== latestRequest.current) return;

        // Without this the list keeps saying "Loading..." for as long as the page is open
        setUsers([]);
        setLoading(false);
        setFailed(true);
      });
    }, 400),
    []
  );

  useEffect(() => {
    // Ahead of the early returns: a stale failure would otherwise outlive the request it came from
    setFailed(false);

    // Anything in flight, or still waiting for the debounce to fire, belongs to a state the panel
    // has moved on from: ignore the one, drop the other before it asks with stale arguments
    latestRequest.current += 1;
    searchUsers.cancel();

    if (onRemoveUser && !includeUserIds?.length) {
      // Nothing is coming back to clear this, since the response above is ignored
      setLoading(false);
      setUsers([]);
      return;
    }

    // An empty allowed list would make ransack drop the filter and return
    // everyone, so don't search at all
    if (allowedUserIds?.length === 0) {
      setLoading(false);
      setUsers([]);
      return;
    }

    setLoading(true);
    searchUsers(searchText, companyId, includeUserIds, excludeUserIds, allowedUserIds);
  }, [searchText, companyId, includeUserIds, excludeUserIds, allowedUserIds]);

  const emptyText = () => {
    if (loading) return 'Loading...';

    if (failed) return 'Users couldn\'t be loaded. Reload the page to try again.';

    if (onRemoveUser && includeUserIds?.length === 0) return 'Start by adding users';

    if (allowedUserIds?.length === 0) {
      return 'Corporate defaults don\'t include any user from this company';
    }

    return 'No Users Found';
  };

  return (
    <GenericList
      emptyText={emptyText()}
      items={users}
      loading={loading}
      renderItemLabel={({ name }) => <div>{name}</div>}
      searchText={searchText}
      onAddItem={onAddUser}
      onRemoveItem={onRemoveUser}
      onSetSearchText={setSearchText}
    />
  );
}
